import axios, { AxiosError } from "axios";
import apiClient from "../services/apiClient";
import {
  VocabularyData,
  DynamoDBItem,
  // TableData,
  CourseData,
  ApiResponse,
  Section,
  LessonData,
  ImageDataUpload,
  ImageUploadResponse,
  PassageData,
  TriviaData,
} from "./types";

// DataLoader API URL: injected by SST at build time per stage (prod/dev)
// Falls back to dev API for local development
export const dataLoaderUri =
  process.env.REACT_APP_API_URL || "https://ob38jihfoj.execute-api.us-east-1.amazonaws.com/dev";
// Mobile API URL: injected by SST at build time per stage (prod/dev)
// Falls back to prod API for local development
export const mobileAppUri =
  process.env.REACT_APP_MOBILE_API_URL || "https://2f23fxa2il.execute-api.us-east-1.amazonaws.com";

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  retryableStatusCodes: [503, 502, 504, 429, 403], // Service unavailable, bad gateway, gateway timeout, too many requests, forbidden (intermittent auth issues)
};

// Utility function to sleep
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Retry wrapper for API calls
async function withRetry<T>(apiCall: () => Promise<T>, operation: string = "API call"): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      const result = await apiCall();
      if (attempt > 0) {
        console.log(`✅ ${operation} succeeded on attempt ${attempt + 1}`);
      }
      return result;
    } catch (error: any) {
      lastError = error;

      // Check if this is a retryable error
      let isRetryable = false;
      let statusCode = 0;

      if (axios.isAxiosError(error) && error.response) {
        // Axios error with response
        statusCode = error.response.status;
        isRetryable = RETRY_CONFIG.retryableStatusCodes.includes(statusCode);
      } else if (axios.isAxiosError(error) && !error.response) {
        // Axios network error (including CORS)
        isRetryable = true;
        statusCode = 0; // Network error
      } else if (error instanceof Error && error.message.includes("HTTP")) {
        // Fetch API error
        const statusMatch = error.message.match(/HTTP (\d+)/);
        if (statusMatch) {
          statusCode = parseInt(statusMatch[1]);
          isRetryable = RETRY_CONFIG.retryableStatusCodes.includes(statusCode);
        }
      } else if (error.name === "TypeError" && error.message.includes("fetch")) {
        // Network error
        isRetryable = true;
        statusCode = 0; // Network error
      } else if (
        error.message &&
        (error.message.toLowerCase().includes("cors") ||
          error.message.toLowerCase().includes("cross-origin") ||
          error.message.toLowerCase().includes("network error") ||
          error.message.toLowerCase().includes("failed to fetch"))
      ) {
        // CORS and network errors
        isRetryable = true;
        statusCode = 0; // CORS/Network error
      }

      // Don't retry if not retryable or on last attempt
      if (!isRetryable || attempt === RETRY_CONFIG.maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(RETRY_CONFIG.baseDelay * Math.pow(2, attempt), RETRY_CONFIG.maxDelay);

      const errorType = statusCode === 0 ? "CORS/Network error" : `status ${statusCode}`;
      console.warn(
        `⚠️ ${operation} failed on attempt ${attempt + 1} with ${errorType}. Retrying in ${delay}ms...`
      );
      await sleep(delay);
    }
  }

  console.error(`❌ ${operation} failed after ${RETRY_CONFIG.maxRetries + 1} attempts`);
  throw lastError;
}

// Fetch course data
export const fetchCourseData = async (courseNumber: string) => {
  return withRetry(async () => {
    const data = await apiClient.get(`${mobileAppUri}/api/v1/lesson`, {
      params: { courseNumber },
    });
    return data;
  }, "fetchCourseData");
};

export async function fetchAllSections(): Promise<string[]> {
  return withRetry(async () => {
    const apiUrl = `${mobileAppUri}/api/v1/all-sections/1`;
    const data: ApiResponse = await apiClient.get(apiUrl);
    const identifiers = data.sections.map((section) => section.Identifier);
    return identifiers;
  }, "fetchAllSections");
}

// // Update course data
// export const updateCourse = async (courseData: CourseData) => {
//   // Note: Map the local Vocabulary_List to the new backend key "VocabularyList"
//   const body = {
//     Identifier: courseData.Identifier,
//     City: courseData.City,
//     Country: courseData.Country,
//     Description: courseData.Description,
//     Images: courseData.Images,
//     VocabularyList: courseData.Vocabulary_List,
//   };

//   try {
//     const response = await axios.put(
//       `${dataLoaderUri}/api/v1/update-course/${courseData.Identifier}`,
//       body,
//       { headers: { "Content-Type": "application/json" } }
//     );
//     console.log(response.data);
//   } catch (error) {
//     console.log("Error updating course:", error);
//   }
// };

export async function fetchSectionData(section: string): Promise<Section[]> {
  try {
    const response = await apiClient.getAxiosInstance().get(`${mobileAppUri}/api/v1/sections`, {
      params: { section },
    });

    const data = response.data.items.map(
      (item: any): Section => ({
        Identifier: item.Identifier.S,
        Lessons: item.Lessons.L.map((lesson: any): LessonData => {
          const imageInfo = lesson.M.ImageInfo?.M || {};
          const imageUrl = imageInfo.ImageUrl?.S;
          const imageObjectsStr = imageInfo.ImageObjects?.S;
          let imageObjects = [];
          if (imageObjectsStr) {
            try {
              imageObjects = JSON.parse(imageObjectsStr);
            } catch (e) {
              console.log("Error parsing ImageObjects:", e);
            }
          }
          const itemDescribeWords = lesson.M.ItemDescribeWords?.L?.map((word: any) => word.S) || [];
          const sentenceToBeTranslated =
            lesson.M.SentenceToBeTranslated?.L?.map((sentence: any) => sentence.S) || [];
          const translatedSentence =
            lesson.M.TranslatedSentence?.L?.map((sentence: any) => sentence.S) || [];
          const additionalWords = lesson.M.AdditionalWords?.L?.map((word: any) => word.S) || [];

          let videoQuestions = undefined;
          if (lesson.M.VideoQuestions) {
            videoQuestions = {
              VideoUrl: lesson.M.VideoQuestions.M.VideoUrl?.S || "",
              Questions:
                lesson.M.VideoQuestions.M.Questions?.L.map((q: any) => ({
                  question: q.M.question?.S || "",
                  correctOption: q.M.correctOption?.S || "",
                  options: q.M.options?.L.slice(0, 3).map((opt: any) => opt.S) || [],
                })) || [],
            };
          }

          const reviewSection =
            lesson.M.Type.S === "Review" ? lesson.M.ReviewSection?.S : undefined;
          const reviewComponent =
            lesson.M.Type.S === "Review" ? lesson.M.ReviewComponent?.S : undefined;

          return {
            Type: lesson.M.Type.S,
            Video: lesson.M.Video?.S,
            VideoContentText: lesson.M.VideoContentText?.S,
            VideoTitle: lesson.M.VideoTitle?.S,
            PassageId: lesson.M.PassageId?.S,
            Objectives: lesson.M.Objectives?.L?.map((objective: any) => objective.S),
            LLM_Prompt: lesson.M.LLM_Prompt?.S,
            Character: lesson.M.Character?.S,
            ImageUrl: imageUrl,
            ImageObjects: imageObjects,
            ItemDescribeWords: itemDescribeWords,
            SentenceToBeTranslated: sentenceToBeTranslated,
            TranslatedSentence: translatedSentence,
            AdditionalWords: additionalWords,
            VideoQuestions: videoQuestions,
            ReviewSection: reviewSection,
            ReviewComponent: reviewComponent,
            imageFile: undefined,
            croppedBlob: undefined,
          };
        }),
      })
    );
    console.log(data);
    return data;
  } catch (err: any) {
    console.log("Error fetching section data:", err);
    throw new Error(err.message);
  }
}

export async function updateSection(identifier: string, sectionData: Section): Promise<void> {
  console.log("Section Data Before Transformation:", sectionData);

  const body = {
    Identifier: { S: identifier },
    Lessons: {
      L: sectionData.Lessons.map((lesson: LessonData) => ({
        M: {
          Type: { S: lesson.Type },
          Video: lesson.Video ? { S: lesson.Video } : undefined,
          VideoTitle: lesson.VideoTitle ? { S: lesson.VideoTitle } : undefined,
          VideoContentText: lesson.VideoContentText ? { S: lesson.VideoContentText } : undefined,
          PassageId: lesson.PassageId ? { S: lesson.PassageId } : undefined,
          ImageInfo:
            lesson.ImageUrl || lesson.ImageObjects
              ? {
                  M: {
                    ImageUrl: lesson.ImageUrl ? { S: lesson.ImageUrl } : undefined,
                    ImageObjects: lesson.ImageObjects
                      ? { S: JSON.stringify(lesson.ImageObjects) }
                      : undefined,
                  },
                }
              : undefined,
          Objectives:
            lesson.Type === "Conversation" && lesson.Objectives
              ? { L: lesson.Objectives.map((obj) => ({ S: obj })) }
              : undefined,
          LLM_Prompt: lesson.LLM_Prompt ? { S: lesson.LLM_Prompt } : undefined,
          Character: lesson.Character ? { S: lesson.Character } : undefined,
          ItemDescribeWords:
            lesson.Type === "ItemDescribe" && lesson.ItemDescribeWords
              ? {
                  L: lesson.ItemDescribeWords.map((word: string) => ({
                    S: word,
                  })),
                }
              : undefined,
          SentenceToBeTranslated:
            lesson.Type === "Sentence_Translation" && lesson.SentenceToBeTranslated
              ? {
                  L: lesson.SentenceToBeTranslated.map((sentence: string) => ({
                    S: sentence,
                  })),
                }
              : undefined,
          TranslatedSentence:
            lesson.Type === "Sentence_Translation" && lesson.TranslatedSentence
              ? {
                  L: lesson.TranslatedSentence.map((sentence: string) => ({
                    S: sentence,
                  })),
                }
              : undefined,
          AdditionalWords:
            lesson.Type === "Sentence_Translation" && lesson.AdditionalWords
              ? {
                  L: lesson.AdditionalWords.map((word: string) => ({
                    S: word,
                  })),
                }
              : undefined,
          VideoQuestions:
            lesson.Type === "Video_Questions" && lesson.VideoQuestions
              ? {
                  M: {
                    VideoUrl: lesson.VideoQuestions.VideoUrl
                      ? { S: lesson.VideoQuestions.VideoUrl }
                      : undefined,
                    Questions: {
                      L: lesson.VideoQuestions.Questions.map((question) => ({
                        M: {
                          question: { S: question.question },
                          correctOption: { S: question.correctOption },
                          options: {
                            L: question.options.slice(0, 3).map((option) => ({ S: option })),
                          },
                        },
                      })),
                    },
                  },
                }
              : undefined,
          ReviewSection:
            lesson.Type === "Review" && lesson.ReviewSection
              ? { S: lesson.ReviewSection }
              : undefined,
          ReviewComponent:
            lesson.Type === "Review" && lesson.ReviewComponent
              ? { S: lesson.ReviewComponent }
              : undefined,
          ShowAdBefore:
            lesson.ShowAdBefore !== undefined ? { BOOL: lesson.ShowAdBefore } : undefined,
        },
      })),
    },
  };

  console.log("API Request Body:", body);

  try {
    const response = await apiClient.put(
      `${dataLoaderUri}/api/v1/update-section/${identifier}`,
      body
    );
    console.log("API Response Status:", response.status || 200);
    console.log("API Response Data:", response);
  } catch (error) {
    console.log("Error updating section:", error);
    throw error;
  }
}

export const uploadReadingImage = async (fileName: string, fileType: string): Promise<string> => {
  const response = await apiClient.post(`${dataLoaderUri}/upload-reading-image`, {
    fileName,
    fileType,
  });
  return response.url;
};

export const addPassage = async (data: PassageData): Promise<any> => {
  try {
    const response = await apiClient.post(`${dataLoaderUri}/api/v1/add-passage`, data);
    return response;
  } catch (error: any) {
    console.error("Error adding passage:", error);
    throw error;
  }
};

export async function uploadImage(fileName: string, file: Blob): Promise<string> {
  const sanitizeFileName = (fileName: string) => fileName.replace(/[^a-zA-Z0-9-_]/g, "_");

  return withRetry(async () => {
    console.log("Uploading image...");
    const sanitizedFileName = sanitizeFileName(fileName);
    const base64Image = await convertToBase64(file);
    const body = { fileName: sanitizedFileName, image: base64Image };

    const response = await apiClient.post<{ url: string }>(
      `${dataLoaderUri}/api/v1/upload-vocab-image`,
      body
    );

    return response.url;
  }, `uploadImage(${fileName})`);
}

export async function uploadVideo(fileName: string, file: Blob): Promise<string> {
  const sanitizeFileName = (fileName: string) => fileName.replace(/[^a-zA-Z0-9-_.]/g, "_");

  return withRetry(async () => {
    console.log("Uploading video via presigned URL...");
    const sanitizedFileName = sanitizeFileName(fileName);

    // Step 1: Get presigned URL from backend
    const presignedResponse = await apiClient.post<{
      uploadUrl: string;
      uploadFields: Record<string, string>;
      s3Url: string;
      s3Key: string;
    }>(`${dataLoaderUri}/api/v1/get-video-upload-url`, {
      fileName: sanitizedFileName,
    });

    console.log("Presigned URL received, uploading to S3...");

    // Step 2: Upload video directly to S3 using presigned URL
    const formData = new FormData();

    // Add all the presigned fields first
    Object.entries(presignedResponse.uploadFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add the file last
    formData.append("file", file);

    // Upload directly to S3 (bypasses API Gateway)
    const uploadResponse = await fetch(presignedResponse.uploadUrl, {
      method: "POST",
      body: formData,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`S3 upload failed: ${uploadResponse.status} - ${errorText}`);
    }

    console.log("Video uploaded successfully to S3");

    // Return the public S3 URL
    return presignedResponse.s3Url;
  }, `uploadVideo(${fileName})`);
}

const convertToBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64String = result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// Call OpenAI API via backend
export const callOpenAI = async (payload: {
  messages: { role: string; content: string }[];
  temperature: number;
  max_tokens: number;
  json_mode?: boolean;
  model?: string;
}) => {
  return withRetry(async () => {
    const backendEndpoint = `${mobileAppUri}/api/v1/genai`;
    const response = await apiClient.post(backendEndpoint, payload);
    const latestResponse = response.response;
    const cost = response.cost;
    const inputTokens = response.input_tokens;
    const outputTokens = response.output_tokens;
    const totalTokens = response.total_tokens;
    const chatTurns = response.chat_turns;
    console.log("OpenAI Response:", latestResponse);
    return {
      response: latestResponse,
      cost: response.cost,
      inputTokens: response.input_tokens,
      outputTokens: response.output_tokens,
      totalTokens: response.total_tokens,
      chatTurns: response.chat_turns,
    };
  }, "callOpenAI").catch((error) => {
    console.error("Error calling backend for OpenAI:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      payload: JSON.stringify(payload, null, 2),
    });
    return null;
  });
};
export const fetchTriviaData = async (section: string): Promise<TriviaData[]> => {
  const uri = `${mobileAppUri}/api/v1/get-trivia?section=${section}`;
  try {
    const data = await apiClient.get(uri);
    return data.map((item: any) => ({
      Identifier: item.identifier?.S ?? "",
      Level: item.level?.S ?? "",
      Section: section,
      Question: item.question?.S ?? "",
      Options: item.options?.L?.map((option: { S: string }) => option.S) ?? [],
      Answer: item.answer?.S ?? "",
      ImageUrl: item.imageUrl?.S ?? "",
    }));
  } catch (error: any) {
    console.error("Error fetching trivia data:", error.message);
    throw new Error(`Failed to fetch trivia: ${error.message}`);
  }
};

export const addTrivia = async (data: TriviaData): Promise<void> => {
  const uri = `${dataLoaderUri}/api/v1/add-trivia`;
  try {
    await apiClient.post(uri, {
      identifier: data.Identifier,
      level: data.Level,
      question: data.Question,
      options: data.Options,
      imageUrl: data.ImageUrl,
      answer: data.Answer,
    });
  } catch (error: any) {
    console.error("Error adding trivia:", error.message);
    throw new Error(`Failed to add trivia: ${error.message}`);
  }
};

export const updateTrivia = async (
  identifier: string,
  level: string,
  data: TriviaData
): Promise<void> => {
  const uri = `${dataLoaderUri}/api/v1/edit-trivia`;
  try {
    await apiClient.put(uri, {
      identifier,
      level,
      question: data.Question,
      options: data.Options,
      answer: data.Answer,
    });
  } catch (error: any) {
    console.error("Error updating trivia:", error.message);
    throw new Error(`Failed to update trivia: ${error.message}`);
  }
};

export const deleteTrivia = async (identifier: string, level: string): Promise<void> => {
  const uri = `${dataLoaderUri}/api/v1/delete-trivia`;
  try {
    await apiClient.request({
      method: "delete",
      url: uri,
      data: { identifier, level },
    });
  } catch (error: any) {
    console.error("Error deleting trivia:", error.message);
    throw new Error(`Failed to delete trivia: ${error.message}`);
  }
};

// Translate text using OpenAI API
export const translateText = async (
  text: string,
  sourceLanguage: string,
  targetLanguage: string
): Promise<string> => {
  return withRetry(async () => {
    const prompt = `Translate the following text from ${sourceLanguage} to ${targetLanguage}. Only return the translated text, no additional commentary:

${text}`;

    const payload = {
      messages: [{ role: "user", content: prompt }],
      temperature: 0.3,
      max_tokens: 1000,
    };

    const result = await callOpenAI(payload);

    if (result && result.response) {
      return result.response.trim();
    } else {
      throw new Error("No translation received from OpenAI");
    }
  }, `translateText(${sourceLanguage}->${targetLanguage})`);
};

export const generateImage = async (
  payload: object // Keep payload type generic or define a specific interface if known
): Promise<string | null> => {
  const endpoint = `${mobileAppUri}/api/v1/imageGen`;

  console.log(`--- Attempting POST to ${endpoint} ---`);
  // 1. Log the payload being sent (CRITICAL FOR DEBUGGING 400 ERRORS)
  console.log("Payload being sent:", JSON.stringify(payload, null, 2));
  console.log("------------------------------------");

  try {
    const response = await apiClient.post(endpoint, payload);

    // 2. Check success status and response structure more carefully
    if (response && typeof response.url === "string") {
      console.log("Image generated successfully. URL:", response.url);
      return response.url;
    } else {
      // Log unexpected success response format
      console.warn(`Image generation API returned unexpected data format:`, response);
      // Treat unexpected success format as an error case
      throw new Error(`Unexpected response format from image generation API`);
    }
  } catch (error) {
    console.error("--- Error during image generation request ---");

    // Check if it's an Axios error to access response details
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      console.error("Axios Error Message:", axiosError.message);
      if (axiosError.response) {
        // 3. Log detailed error response from server (CRITICAL FOR DEBUGGING 400/503)
        console.error("Server Response Status:", axiosError.response.status);
        console.error("Server Response Data:", JSON.stringify(axiosError.response.data, null, 2)); // <<< This often contains validation details for 400 errors
        console.error("Server Response Headers:", axiosError.response.headers);
      } else if (axiosError.request) {
        // Request was made but no response received (network error, timeout)
        console.error("Request was made, but no response received:", axiosError.request);
      }
    } else {
      // Non-Axios error (e.g., issue setting up the request)
      console.error("Non-Axios Error:", error);
    }
    console.error("-----------------------------------------");

    // 4. Option A: Return null (as originally done)
    // return null;

    // 4. Option B: Re-throw the error (recommended for better context in calling function)
    // This allows the `catch` block in `generateImageForVocabulary` to see the original error details.
    throw error;
  }
};

// Section Media API functions
export const fetchSectionsForMedia = async (targetLanguage: string): Promise<any> => {
  return withRetry(async () => {
    const response = await apiClient.get(`${dataLoaderUri}/api/v1/all-sections/${targetLanguage}`);
    return response;
  }, `fetchSectionsForMedia(${targetLanguage})`);
};

export const fetchSectionMedia = async (
  baseLanguage: string,
  targetLanguage: string,
  contentType?: "notes" | "video"
): Promise<any> => {
  return withRetry(
    async () => {
      const params: any = { baseLanguage, targetLanguage };
      if (contentType) {
        params.contentType = contentType;
      }
      const response = await apiClient.get(`${dataLoaderUri}/api/v1/section-media`, { params });
      return response;
    },
    `fetchSectionMedia(${baseLanguage}->${targetLanguage}${contentType ? `, type:${contentType}` : ""})`
  );
};

export const createSectionMedia = async (payload: any): Promise<any> => {
  return withRetry(
    async () => {
      const response = await apiClient.post(`${dataLoaderUri}/api/v1/section-media`, payload);
      return response;
    },
    `createSectionMedia(${payload.sectionId || "unknown"})`
  );
};

export const updateSectionMedia = async (sectionId: string, payload: any): Promise<any> => {
  return withRetry(async () => {
    const response = await apiClient.put(
      `${dataLoaderUri}/api/v1/section-media/${sectionId}`,
      payload
    );
    return response;
  }, `updateSectionMedia(${sectionId})`);
};

export const deleteSectionMedia = async (
  sectionId: string,
  baseLanguage: string,
  targetLanguage: string,
  contentType?: "notes" | "video"
): Promise<any> => {
  return withRetry(
    async () => {
      let url = `${dataLoaderUri}/api/v1/section-media/${sectionId}?baseLanguage=${baseLanguage}&targetLanguage=${targetLanguage}`;
      if (contentType) {
        url += `&contentType=${contentType}`;
      }
      const response = await apiClient.delete(url);
      return response;
    },
    `deleteSectionMedia(${sectionId}${contentType ? `, type:${contentType}` : ""})`
  );
};

// Text-to-Speech synthesis
export const synthesizeSpeech = async (text: string, targetLanguage: string): Promise<any> => {
  return withRetry(async () => {
    const data = await apiClient.post(`${mobileAppUri}/api/v1/synthesize`, {
      text: text,
      targetLanguage: targetLanguage,
    });
    return data;
  }, `synthesizeSpeech(${targetLanguage})`);
};

// Multi-language video generation workflow
export const generateMultiLanguageVideos = async (
  imageUrl: string,
  text: string,
  baseLanguages: string[] = ["EN", "ES", "FR", "DE", "IT", "PT"]
): Promise<any> => {
  try {
    console.log("Starting multi-language video generation workflow...");

    const results = [];

    for (const language of baseLanguages) {
      console.log(`Processing ${language}...`);

      // Step 1: Translate text to target language
      let translatedText = text;
      if (language !== "EN") {
        console.log(`Translating to ${language}...`);
        translatedText = await translateText(text, "EN", language);
      }

      // Step 2: Synthesize speech
      console.log(`Synthesizing speech for ${language}...`);
      const synthResult = await synthesizeSpeech(translatedText, language);

      // Step 3: Extract audio URL from synthesis result
      const audioS3Key = synthResult.data?.s3ObjectKey;
      if (!audioS3Key) {
        throw new Error(`No audio generated for ${language}`);
      }

      // Audio URL - assuming the S3 bucket is accessible via HTTPS
      const audioUrl = `https://spanish-speech-storage.s3.amazonaws.com/${audioS3Key}`;

      results.push({
        language,
        translatedText,
        audioUrl,
        audioS3Key,
        visemes: synthResult.data?.visemes || [],
      });

      console.log(`Completed ${language}: ${audioUrl}`);
    }

    return {
      success: true,
      results,
      imageUrl,
    };
  } catch (error: any) {
    console.error("Error in multi-language video generation:", error);
    throw error;
  }
};

// Generate video from image and audio
export const generateVideo = async (
  imageUrl: string,
  audioUrl: string,
  outputFilename?: string,
  duration?: number
): Promise<any> => {
  return withRetry(
    async () => {
      const response = await apiClient.post(`${dataLoaderUri}/api/v1/generate-video`, {
        imageUrl,
        audioUrl,
        outputFilename,
        duration,
      });
      return response;
    },
    `generateVideo(${outputFilename || "unnamed"})`
  );
};

// Complete workflow: Generate audio for all languages except source (with placeholder video support)
export const generateSectionVideos = async (
  imageUrl: string,
  text: string,
  sectionId: string,
  sourceLanguage: string,
  baseLanguage: string,
  targetLanguage: string,
  allLanguages: string[] = ["EN", "ES", "FR", "DE", "IT", "PT"]
): Promise<any> => {
  try {
    console.log(`Starting audio generation for section ${sectionId}...`);
    console.log(
      `Source language: ${sourceLanguage}, Base: ${baseLanguage}, Target: ${targetLanguage}`
    );

    // Generate audio for all languages except the source language
    const languagesToGenerate = allLanguages.filter((lang) => lang !== sourceLanguage);
    console.log(`Generating audio for languages: ${languagesToGenerate.join(", ")}`);

    // Step 1: Generate multi-language audio (no video generation)
    const audioResults = await generateMultiLanguageVideos(imageUrl, text, languagesToGenerate);

    // Step 2: Process audio results and create media entries
    const mediaResults = [];
    const sectionMediaEntries = [];

    for (const audioResult of audioResults.results) {
      console.log(`Processing audio for ${audioResult.language}...`);

      // Create placeholder video URL for future use (maintaining DB structure)
      const placeholderVideoUrl = `${imageUrl}?audio=${encodeURIComponent(audioResult.audioUrl)}&lang=${audioResult.language}`;

      const mediaData = {
        language: audioResult.language,
        translatedText: audioResult.translatedText,
        audioUrl: audioResult.audioUrl,
        videoUrl: placeholderVideoUrl, // Placeholder for future video implementation
        imageUrl: imageUrl,
        s3Key: audioResult.audioS3Key,
        visemes: audioResult.visemes,
      };

      mediaResults.push(mediaData);

      // Step 3: Create section media entry for each audio/image pair
      const sectionMediaPayload = {
        sectionId: sectionId,
        baseLanguage: baseLanguage,
        targetLanguage: audioResult.language,
        mediaType: "video", // Keep as 'video' for DB structure consistency
        mediaUrl: placeholderVideoUrl, // Placeholder video URL
        text: audioResult.translatedText,
        audioUrl: audioResult.audioUrl,
        imageUrl: imageUrl,
        s3Key: audioResult.audioS3Key,
        visemes: audioResult.visemes,
      };

      console.log(`Saving section media for ${audioResult.language}...`);
      const sectionMediaResult = await createSectionMedia(sectionMediaPayload);
      sectionMediaEntries.push(sectionMediaResult);

      console.log(`Audio generated and saved for ${audioResult.language}: ${audioResult.audioUrl}`);
    }

    return {
      success: true,
      videos: mediaResults, // Keep name for compatibility but contains audio+image data
      sectionMediaEntries: sectionMediaEntries,
      imageUrl,
      originalText: text,
      languagesGenerated: languagesToGenerate,
    };
  } catch (error: any) {
    console.error("Error in complete section audio generation:", error);
    throw error;
  }
};
